import type {
  ArchivedAsset,
  ArchivedAssetMatch,
  Asset,
  AssetAttachment,
  AssetCreateForm,
  AssetCreateInput,
  AssetDetail,
  AssetDetailAttribute,
  AssetFormField,
  AssetListItem,
  AssetUpdateInput,
} from "../domain";
import type {
  ArchivedAssetListItemDto,
  AssetArchivedMatchDto,
  AssetAttachmentDto,
  AssetCreateDto,
  AssetCreateFormDto,
  AssetDetailAttributeDto,
  AssetDetailDto,
  AssetDto,
  AssetFormFieldDto,
  AssetListItemDto,
  AssetUpdateDto,
} from "./assetDtos";

export function assetAttachmentFromDto(dto: AssetAttachmentDto): AssetAttachment {
  return {
    id: dto.id,
    fileUrl: dto.fileUrl,
    extension: dto.extension,
    createdAt: dto.createdAt,
  };
}

export function assetFromDto(dto: AssetDto): Asset {
  return {
    id: dto.id,
    code: dto.code,
    nameEn: dto.nameEn,
    nameAr: dto.nameAr,
    notes: dto.notes,
    status: dto.status ?? "ACTIVE",
    attributes: dto.attributes,
    attachments: (dto.attachments ?? []).map(assetAttachmentFromDto),
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
    accountId: dto.accountId,
    assetTypeId: dto.assetTypeId,
    createdById: dto.createdById ?? null,
    updatedById: dto.updatedById ?? null,
  };
}

export function assetCreateToDto(input: AssetCreateInput): AssetCreateDto {
  return {
    ...(input.accountId !== undefined ? { accountId: input.accountId } : {}),
    assetTypeId: input.assetTypeId,
    branchId: input.branchId,
    nameEn: input.nameEn.trim(),
    nameAr: input.nameAr.trim(),
    ...(input.notes && input.notes.trim().length > 0
      ? { notes: input.notes.trim() }
      : {}),
    attributes: input.attributes,
  };
}

export function assetUpdateToDto(input: AssetUpdateInput): AssetUpdateDto {
  return {
    ...(input.status !== undefined ? { status: input.status } : {}),
    ...(input.nameEn !== undefined ? { nameEn: input.nameEn.trim() } : {}),
    ...(input.nameAr !== undefined ? { nameAr: input.nameAr.trim() } : {}),
    ...(input.notes !== undefined ? { notes: input.notes.trim() } : {}),
    ...(input.branchId !== undefined ? { branchId: input.branchId } : {}),
    ...(input.attributes !== undefined ? { attributes: input.attributes } : {}),
  };
}

export function assetDetailAttributeFromDto(
  dto: AssetDetailAttributeDto,
): AssetDetailAttribute {
  return {
    id: dto.id,
    nameEn: dto.nameEn,
    nameAr: dto.nameAr,
    type: dto.type,
    isRequired: dto.isRequired,
    isUnique: dto.isUnique,
    sortOrder: dto.sortOrder,
    value: dto.value,
  };
}

export function assetDetailFromDto(dto: AssetDetailDto): AssetDetail {
  return {
    id: dto.id,
    code: dto.code,
    nameEn: dto.nameEn,
    nameAr: dto.nameAr,
    notes: dto.notes,
    status: dto.status,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
    account: dto.account,
    branch: dto.branch,
    assetType: {
      ...dto.assetType,
      attachmentsAllowed: dto.assetType.attachmentsAllowed ?? false,
    },
    attributes: dto.attributes.map(assetDetailAttributeFromDto),
    attachments: (dto.attachments ?? []).map(assetAttachmentFromDto),
  };
}

export function assetFormFieldFromDto(dto: AssetFormFieldDto): AssetFormField {
  return {
    key: dto.key,
    source: dto.source,
    fieldType: dto.fieldType,
    nameEn: dto.nameEn,
    nameAr: dto.nameAr,
    isRequired: dto.isRequired,
    isUnique: dto.isUnique,
    sortOrder: dto.sortOrder,
    ...(dto.validation !== undefined ? { validation: dto.validation } : {}),
  };
}

export function assetCreateFormFromDto(
  dto: AssetCreateFormDto,
): AssetCreateForm {
  return {
    assetType: dto.assetType,
    fields: dto.fields.map(assetFormFieldFromDto),
  };
}

export function assetListItemFromDto(dto: AssetListItemDto): AssetListItem {
  return {
    id: dto.id,
    code: dto.code,
    nameEn: dto.nameEn,
    nameAr: dto.nameAr,
    status: dto.status,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
    account: dto.account,
    assetType: dto.assetType,
    branch: dto.branch,
  };
}

export function assetArchivedMatchFromDto(
  dto: AssetArchivedMatchDto,
): ArchivedAssetMatch {
  return {
    id: dto.id,
    ...(dto.nameEn !== undefined ? { nameEn: dto.nameEn } : {}),
    ...(dto.nameAr !== undefined ? { nameAr: dto.nameAr } : {}),
  };
}

export function archivedAssetFromDto(
  dto: ArchivedAssetListItemDto,
): ArchivedAsset {
  return {
    id: dto.id,
    code: dto.code,
    nameEn: dto.nameEn,
    nameAr: dto.nameAr,
    status: dto.status,
    account: dto.account,
    assetType: dto.assetType,
    archivedAt: dto.archivedAt,
  };
}
